import React, { useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import EmptySection from '../../../atoms/EmptySection'
import { ADMIN_ROUTES } from '../../../../constants'
import { usePolicy } from '../../../../hooks/usePolicy'
import { useDeactivateUserMutation, useSearchWealthOwners } from '../../../../api/users'
import WealthOwnerDetailsHeader from './Header'
import WealthOwnerDetailTabs from './WealthOwnerDetailTabs'
import { WealthOwnerDetailsProvider } from './ClientDetailsFormContext'
import SaveCancelFooter from './SaveCancelFooter'

const useStyles = makeStyles({
  detailsPage: {
    margin: '1rem'
  }
})

const useExternalUserId = () => {
  const { externalUserId } = useParams()
  return !externalUserId ? '-1' : externalUserId
}

const useDeleteUser = (wealthOwner) => {
  const { mutateAsync: deleteUser, isLoading: isDeleting } = useDeactivateUserMutation()
  const history = useHistory()
  const handleDelete = useCallback(async () => {
    await deleteUser(wealthOwner)
    // await invalidateSearch()
    history.push(`${ADMIN_ROUTES.CLIENTS}?tab=users`)
  }, [wealthOwner, deleteUser, history])

  return { handleDelete, isDeleting }
}

const useWealthOwner = () => {
  const externalUserId = useExternalUserId()
  const query = useMemo(() => ({
    filters: {
      externalUserId
    },
    includes: {
      role: true
    }
  }), [externalUserId])
  const { data, isLoading, refetch } = useSearchWealthOwners(query)

  const wealthOwner = useMemo(() => {
    return data?.length ? data[0] : null
  }, [data])

  const { handleDelete, isDeleting } = useDeleteUser(wealthOwner)

  return {
    wealthOwner,
    isLoading,
    refetch,
    handleDelete,
    isDeleting
  }
}

function WealthOwnerDetails () {
  const classes = useStyles()
  const canView = usePolicy('admin_view_wo_users')
  const { wealthOwner, isLoading, refetch, handleDelete, isDeleting } = useWealthOwner()

  if (!canView) {
    return (
      <EmptySection
        title='Access Denied'
        description='You do not have sufficient permissions to access this resource'
      />
    )
  }

  if (isLoading) {
    return null
  }

  if (!wealthOwner) {
    return (
      <EmptySection
        title='Wealth Owner Not Found'
        description=''
      />
    )
  }

  if (isDeleting) {
    return (
      <EmptySection
        title='Deactivating Wealth Owner'
        description='Deactivation in progress'
      />
    )
  }

  return (
    <div className={classes.detailsPage}>
      <WealthOwnerDetailsProvider
        wealthOwner={wealthOwner}
        refetch={refetch}
        deleteWealthOwner={handleDelete}
      >
        <WealthOwnerDetailsHeader />
        <WealthOwnerDetailTabs />
        <SaveCancelFooter />
      </WealthOwnerDetailsProvider>
    </div>
  )
}

WealthOwnerDetails.propTypes = {
}

export default WealthOwnerDetails
